import computedLocale from '@/common/utils/computedLocale';
import i18n from '@/common/i18n';
import { ComputeStorage } from '@schema';
import { uniq } from 'lodash-es';
import { UNTOUCHED_PASSWORD_VALUE } from '@/common/constants/common.constants';
import {
  STATUS_ICON_CHECK_O,
  STATUS_ICON_INACTIVE,
  STATUS_ICON_LOADING,
  STATUS_ICON_TIMES_O,
  STATUS_ICON_WARNING_O
} from '@/common/utils/statuses';

export const EXTERNAL_STORAGE_STATUS_ICON_MAP = {
  [ComputeStorage.status.AVAILABLE]: STATUS_ICON_CHECK_O,
  [ComputeStorage.status.DISABLED]: STATUS_ICON_INACTIVE,
  [ComputeStorage.status.RECONFIGURING]: STATUS_ICON_LOADING,
  [ComputeStorage.status.DEGRADED]: STATUS_ICON_WARNING_O,
  [ComputeStorage.status.CRITICAL]: STATUS_ICON_TIMES_O
};

export const EXTERNAL_STORAGE_STATUS_TEXT = computedLocale(() => ({
  [ComputeStorage.status.AVAILABLE]: i18n.t('Available'),
  [ComputeStorage.status.DISABLED]: i18n.t('Disabled'),
  [ComputeStorage.status.RECONFIGURING]: i18n.t('Reconfiguring'),
  [ComputeStorage.status.DEGRADED]: i18n.t('Degraded'),
  [ComputeStorage.status.CRITICAL]: i18n.t('Critical')
}));

export const PROTOCOLS = {
  ISCSI: 'iscsi',
  NFS: 'nfs',
  FIBRE_CHANNEL: 'fc'
} as const;

export const PROTOCOL_LABELS = computedLocale(() => ({
  [PROTOCOLS.ISCSI]: i18n.t('iSCSI'),
  [PROTOCOLS.NFS]: i18n.t('NFS'),
  [PROTOCOLS.FIBRE_CHANNEL]: i18n.t('Fibre Channel')
}));

export enum EXTERNAL_STORAGES {
  PURE_STORAGE = 'pure',
  NETAPP = 'netapp',
  OPENSTACK_COMPATIBLE = 'openstack'
}

export const EXTERNAL_STORAGE_LABELS = computedLocale<Record<EXTERNAL_STORAGES, string>>(() => ({
  [EXTERNAL_STORAGES.PURE_STORAGE]: i18n.t('Pure Storage{trademark}', { trademark: '®' }),
  [EXTERNAL_STORAGES.NETAPP]: i18n.t('NetApp{trademark}', { trademark: '®' }),
  [EXTERNAL_STORAGES.OPENSTACK_COMPATIBLE]: i18n.t('OpenStack-compatible storage')
}));

export const VOLUME_DRIVERS = {
  NETAPP: 'cinder.volume.drivers.netapp.common.NetAppDriver',
  PURE_ISCSI: 'cinder.volume.drivers.pure.PureISCSIDriver',
  PURE_FC: 'cinder.volume.drivers.pure.PureFCDriver',
  NFS: 'cinder.volume.drivers.nfs.NfsDriver'
};

export const REVERT_TO_SNAPSHOT_SUPPORTED_DRIVERS: readonly string[] = [
  'cinder.volume.drivers.dell_emc.powermax.fc.PowerMaxFCDriver',
  'cinder.volume.drivers.dell_emc.powermax.iscsi.PowerMaxISCSIDriver',
  'cinder.volume.drivers.dell_emc.powerstore.driver.PowerStoreDriver',
  'cinder.volume.drivers.dell_emc.unity.Driver',
  'cinder.volume.drivers.dell_emc.vnx.driver.VNXDriver',
  'cinder.volume.drivers.hitachi.hbsd_fc.HBSDFCDriver',
  'cinder.volume.drivers.hitachi.hbsd_iscsi.HBSDISCSIDriver',
  'cinder.volume.drivers.hpe.hpe_3par_fc.HPE3PARFCDriver',
  'cinder.volume.drivers.hpe.hpe_3par_iscsi.HPE3PARISCSIDriver',
  'cinder.volume.drivers.ibm.ibm_storage.ibm_storage.IBMStorageDriver',
  'cinder.volume.drivers.ibm.storwize_svc.storwize_svc_fc.StorwizeSVCFCDriver',
  'cinder.volume.drivers.ibm.storwize_svc.storwize_svc_iscsi.StorwizeSVCISCSIDriver',
  'cinder.volume.drivers.infinidat.InfiniboxVolumeDriver',
  VOLUME_DRIVERS.NETAPP,
  'cinder.volume.drivers.nexenta.ns5.iscsi.NexentaISCSIDriver',
  'cinder.volume.drivers.nimble.NimbleFCDriver',
  'cinder.volume.drivers.nimble.NimbleISCSIDriver',
  'cinder.volume.drivers.open_e.iscsi.JovianISCSIDriver',
  VOLUME_DRIVERS.PURE_FC,
  VOLUME_DRIVERS.PURE_ISCSI,
  'cinder.volume.drivers.solidfire.SolidFireDriver',
  'cinder.volume.drivers.vzstorage.VZStorageDriver'
];

export const VOLUME_DRIVER_LABELS = computedLocale(() => ({
  [VOLUME_DRIVERS.NETAPP]: i18n.t('NetApp'),
  [VOLUME_DRIVERS.PURE_ISCSI]: i18n.t('Pure Storage'),
  [VOLUME_DRIVERS.PURE_FC]: i18n.t('Pure Storage'),
  [VOLUME_DRIVERS.NFS]: i18n.t('NFS')
}));

export const VOLUME_DRIVER_EXTERNAL_STORAGES_MAP = {
  [EXTERNAL_STORAGES.PURE_STORAGE]: {
    [PROTOCOLS.ISCSI]: VOLUME_DRIVERS.PURE_ISCSI,
    [PROTOCOLS.FIBRE_CHANNEL]: VOLUME_DRIVERS.PURE_FC
  },
  [EXTERNAL_STORAGES.NETAPP]: VOLUME_DRIVERS.NETAPP,
  [EXTERNAL_STORAGES.OPENSTACK_COMPATIBLE]: ''
};

export const VOLUME_DRIVER_TO_STORAGE_MAP = {
  [VOLUME_DRIVERS.PURE_ISCSI]: EXTERNAL_STORAGES.PURE_STORAGE,
  [VOLUME_DRIVERS.PURE_FC]: EXTERNAL_STORAGES.PURE_STORAGE,
  [VOLUME_DRIVERS.NETAPP]: EXTERNAL_STORAGES.NETAPP
};

export const NFS_VERSIONS = {
  NFS_3: 'vers=3',
  NFS_4_1: 'vers=4.1'
};

export const NFS_VERSIONS_LABELS = computedLocale(() => ({
  [NFS_VERSIONS.NFS_3]: i18n.t('NFS 3'),
  [NFS_VERSIONS.NFS_4_1]: i18n.t('NFS 4.1'),
}));

export const TRANSPORT_TYPE = {
  HTTPS: 'https',
  HTTP: 'http'
} as const;

export const TRANSPORT_TYPE_LABELS = {
  [TRANSPORT_TYPE.HTTP]: 'HTTP',
  [TRANSPORT_TYPE.HTTPS]: 'HTTPS'
};

export const UNKNOWN_SECRET_PARAM_VALUE = UNTOUCHED_PASSWORD_VALUE;

export const ISCSI_DRIVERS = {
  'DataCore SANsymphony iSCSI': {
    volume_driver: 'cinder.volume.drivers.datacore.iscsi.ISCSIVolumeDriver'
  },
  'Datera iSCSI': {
    volume_driver: 'cinder.volume.drivers.datera.datera_iscsi.DateraDriver'
  },
  'Dell PowerMax iSCSI': {
    volume_driver: 'cinder.volume.drivers.dell_emc.powermax.iscsi.PowerMaxISCSIDriver'
  },
  'Dell PowerStore iSCSI': {
    volume_driver: 'cinder.volume.drivers.dell_emc.powerstore.driver.PowerStoreDriver',
    storage_protocol: 'iscsi'
  },
  'Dell PowerVault ME iSCSI': {
    volume_driver: 'cinder.volume.drivers.dell_emc.powervault.iscsi.PVMEISCSIDriver',
    driver_use_ssl: true
  },
  'Dell Unity iSCSI': {
    volume_driver: 'cinder.volume.drivers.dell_emc.unity.Driver',
    storage_protocol: 'iscsi'
  },
  'Dell VNX iSCSI': {
    volume_driver: 'cinder.volume.drivers.dell_emc.vnx.driver.VNXDriver',
    storage_protocol: 'iscsi'
  },
  'Dell XtremIO iSCSI': {
    volume_driver: 'cinder.volume.drivers.dell_emc.xtremio.XtremIOISCSIDriver'
  },
  'Dell SC Series iSCSI': {
    volume_driver: 'cinder.volume.drivers.dell_emc.sc.storagecenter_iscsi.SCISCSIDriver'
  },
  'Fujitsu Eternus DX iSCSI': {
    volume_driver: 'cinder.volume.drivers.fujitsu.eternus_dx.eternus_dx_iscsi.FJDXISCSIDriver'
  },
  'Hedvig iSCSI': {
    volume_driver: 'cinder.volume.drivers.hedvig.hedvig_cinder.HedvigISCSIDriver'
  },
  'Hitachi VSP iSCSI': {
    volume_driver: 'cinder.volume.drivers.hitachi.hbsd_iscsi.HBSDISCSIDriver',
    hitachi_mirror_ssl_cert_verify: true
  },
  'HPE MSA iSCSI': {
    volume_driver: 'cinder.volume.drivers.san.hp.hpmsa_iscsi.HPMSAISCSIDriver',
    driver_use_ssl: true
  },
  'HPE 3PAR iSCSI': {
    volume_driver: 'cinder.volume.drivers.hpe.hpe_3par_iscsi.HPE3PARISCSIDriver'
  },
  'Huawei iSCSI': {
    volume_driver: 'cinder.volume.drivers.huawei_official.huawei_driver.HuaweiISCSIDriver'
  },
  'Huawei iSCSI (legacy)': {
    volume_driver: 'cinder.volume.drivers.huawei.huawei_driver.HuaweiISCSIDriver'
  },
  'IBM FlashSystem iSCSI': {
    volume_driver: 'cinder.volume.drivers.ibm.flashsystem_iscsi.FlashSystemISCSIDriver',
    flashsystem_connection_protocol: 'iscsi'
  },
  'IBM Spectrum Virtualize iSCSI': {
    volume_driver: 'cinder.volume.drivers.ibm.storwize_svc.storwize_svc_iscsi.StorwizeSVCISCSIDriver'
  },
  'Infinidat iSCSI': {
    volume_driver: 'cinder.volume.drivers.infinidat.InfiniboxVolumeDriver',
    infinidat_storage_protocol: 'iscsi',
    driver_use_ssl: true
  },
  'Infortrend iSCSI': {
    volume_driver: 'cinder.volume.drivers.infortrend.infortrend_iscsi_cli.InfortrendCLIISCSIDriver'
  },
  'Inspur AS13000 iSCSI': {
    volume_driver: 'cinder.volume.drivers.inspur.as13000.as13000_driver.AS13000Driver'
  },
  'Inspur InStorage iSCSI': {
    volume_driver: 'cinder.volume.drivers.inspur.instorage.instorage_iscsi.InStorageMCSISCSIDriver'
  },
  'Kaminario K2 iSCSI': {
    volume_driver: 'cinder.volume.drivers.kaminario.kaminario_iscsi.KaminarioISCSIDriver'
  },
  'Lenovo iSCSI': {
    volume_driver: 'cinder.volume.drivers.lenovo.lenovo_iscsi.LenovoISCSIDriver',
    driver_use_ssl: true
  },
  'MacroSAN iSCSI': {
    volume_driver: 'cinder.volume.drivers.macrosan.driver.MacroSANISCSIDriver'
  },
  'NEC Storage M iSCSI': {
    volume_driver: 'cinder.volume.drivers.nec.volume.MStorageISCSIDriver'
  },
  'NexentaStor 4.x iSCSI': {
    volume_driver: 'cinder.volume.drivers.nexenta.iscsi.NexentaISCSIDriver'
  },
  'NexentaStor 5.x iSCSI': {
    volume_driver: 'cinder.volume.drivers.nexenta.ns5.iscsi.NexentaISCSIDriver'
  },
  'Nimble & Alletra 6k iSCSI': {
    volume_driver: 'cinder.volume.drivers.nimble.NimbleISCSIDriver'
  },
  'Open-E JovianDSS iSCSI': {
    volume_driver: 'cinder.volume.drivers.open_e.iscsi.JovianISCSIDriver',
    driver_use_ssl: true
  },
  'ProphetStor iSCSI': {
    volume_driver: 'cinder.volume.drivers.prophetstor.dpl_iscsi.DPLISCSIDriver'
  },
  'SandStone iSCSI': {
    volume_driver: 'cinder.volume.drivers.sandstone.sds_driver.SdsISCSIDriver'
  },
  'Seagate Array iSCSI': {
    volume_driver: 'cinder.volume.drivers.stx.iscsi.STXISCSIDriver',
    driver_use_ssl: true
  },
  'SolidFire iSCSI': {
    volume_driver: 'cinder.volume.drivers.solidfire.SolidFireDriver'
  },
  'Synology DSM iSCSI': {
    volume_driver: 'cinder.volume.drivers.synology.synology_iscsi.SynoISCSIDriver',
    driver_use_ssl: true,
    synology_ssl_verify: true
  },
  'TOYOU NetStor iSCSI': {
    volume_driver: 'cinder.volume.drivers.toyou.acs5000.acs5000_iscsi.Acs5000ISCSIDriver'
  },
  'Veritas ACCESS iSCSI': {
    volume_driver: 'cinder.volume.drivers.veritas_access.veritas_iscsi.ACCESSIscsiDriver'
  },
  'Zadara Storage VPSA iSCSI': {
    volume_driver: 'cinder.volume.drivers.zadara.zadara.ZadaraVPSAISCSIDriver',
    zadara_vpsa_use_ssl: true,
    zadara_ssl_cert_verify: true
  }
};

export const FC_DRIVERS = {
  'DataCore SANsymphony FC': {
    volume_driver: 'cinder.volume.drivers.datacore.fc.FibreChannelVolumeDriver'
  },
  'Dell PowerMax FC': {
    volume_driver: 'cinder.volume.drivers.dell_emc.powermax.fc.PowerMaxFCDriver'
  },
  'Dell PowerStore FC': {
    volume_driver: 'cinder.volume.drivers.dell_emc.powerstore.driver.PowerStoreDriver',
    storage_protocol: 'fc'
  },
  'Dell PowerVault ME FC': {
    volume_driver: 'cinder.volume.drivers.dell_emc.powervault.fc.PVMEFCDriver'
  },
  'Dell Unity FC': {
    volume_driver: 'cinder.volume.drivers.dell_emc.unity.Driver',
    storage_protocol: 'fc'
  },
  'Dell VNX FC': {
    volume_driver: 'cinder.volume.drivers.dell_emc.vnx.driver.VNXDriver',
    storage_protocol: 'fc'
  },
  'Dell XtremIO FC': {
    volume_driver: 'cinder.volume.drivers.dell_emc.xtremio.XtremIOFibreChannelDriver'
  },
  'Dell SC Series FC': {
    volume_driver: 'cinder.volume.drivers.dell_emc.sc.storagecenter_fc.SCFCDriver'
  },
  'Fujitsu Eternus DX FC': {
    volume_driver: 'cinder.volume.drivers.fujitsu.eternus_dx.eternus_dx_fc.FJDXFCDriver'
  },
  'Hitachi VSP FC': {
    volume_driver: 'cinder.volume.drivers.hitachi.hbsd_fc.HBSDFCDriver'
  },
  'HPE MSA FC': {
    volume_driver: 'cinder.volume.drivers.san.hp.hpmsa_fc.HPMSAFCDriver'
  },
  'HPE 3PAR FC': {
    volume_driver: 'cinder.volume.drivers.hpe.hpe_3par_fc.HPE3PARFCDriver'
  },
  'Huawei FC': {
    volume_driver: 'cinder.volume.drivers.huawei_official.huawei_driver.HuaweiFCDriver'
  },
  'Huawei FC (legacy)': {
    volume_driver: 'cinder.volume.drivers.huawei.huawei_driver.HuaweiFCDriver'
  },
  'IBM FlashSystem FC': {
    volume_driver: 'cinder.volume.drivers.ibm.flashsystem_fc.FlashSystemFCDriver',
    flashsystem_connection_protocol: 'fc'
  },
  'IBM Storage FC': {
    volume_driver: 'cinder.volume.drivers.ibm.ibm_storage.IBMStorageDriver',
    connection_type: 'fibre_channel'
  },
  'IBM Spectrum Virtualize FC': {
    volume_driver: 'cinder.volume.drivers.ibm.storwize_svc.storwize_svc_fc.StorwizeSVCFCDriver'
  },
  'Infinidat FC': {
    volume_driver: 'cinder.volume.drivers.infinidat.InfiniboxVolumeDriver',
    infinidat_storage_protocol: 'fc'
  },
  'Infortrend FC': {
    volume_driver: 'cinder.volume.drivers.infortrend.infortrend_fc_cli.InfortrendCLIFCDriver'
  },
  'Inspur InStorage FC': {
    volume_driver: 'cinder.volume.drivers.inspur.instorage.instorage_fc.InStorageMCSFCDriver'
  },
  'Kaminario K2 FC': {
    volume_driver: 'cinder.volume.drivers.kaminario.kaminario_fc.KaminarioFCDriver'
  },
  'Lenovo FC': {
    volume_driver: 'cinder.volume.drivers.lenovo.lenovo_fc.LenovoFCDriver'
  },
  'MacroSAN FC': {
    volume_driver: 'cinder.volume.drivers.macrosan.driver.MacroSANFCDriver'
  },
  'NEC Storage M FC': {
    volume_driver: 'cinder.volume.drivers.nec.volume.MStorageFCDriver'
  },
  'Nimble & Alletra 6k FC': {
    volume_driver: 'cinder.volume.drivers.nimble.NimbleFCDriver'
  },
  'ProphetStor FC': {
    volume_driver: 'cinder.volume.drivers.prophetstor.dpl_fc.DPLFCDriver'
  },
  'Seagate Array FC': {
    volume_driver: 'cinder.volume.drivers.stx.fc.STXFCDriver'
  }
};

export const NON_STANDARD_MANAGEMENT_ADDRESS_MAPPING = {
  'NetApp iSCSI': 'netapp_server_hostname',
  'NetApp FC': 'netapp_server_hostname',
  'Fujitsu Eternus DX iSCSI': null,
  'Fujitsu Eternus DX FC': null,
  'NEC Storage M iSCSI': 'nec_ismcli_fip',
  'NEC Storage M FC': 'nec_ismcli_fip',
  'NexentaStor 4.x iSCSI': 'nexenta_host',
  'NexentaStor 5.x iSCSI': 'nexenta_host',
  'Open-E JovianDSS iSCSI': 'san_hosts',
  'Zadara Storage VPSA iSCSI': 'zadara_vpsa_host'
};

export const NON_STANDARD_DRIVER_PROTOCOL_FIELD_MAPPING = {
  'cinder.volume.drivers.ibm.flashsystem_iscsi.FlashSystemISCSIDriver': 'flashsystem_connection_protocol',
  'cinder.volume.drivers.ibm.flashsystem_fc.FlashSystemFCDriver': 'flashsystem_connection_protocol',
  'cinder.volume.drivers.ibm.ibm_storage.IBMStorageDriver': 'connection_type',
  'cinder.volume.drivers.infinidat.InfiniboxVolumeDriver': 'infinidat_storage_protocol',
  'cinder.volume.drivers.dell_emc.powerstore.driver.PowerStoreDriver': 'storage_protocol',
  'cinder.volume.drivers.dell_emc.unity.Driver': 'storage_protocol',
  'cinder.volume.drivers.dell_emc.vnx.driver.VNXDriver': 'storage_protocol'
};

export const NON_STANDARD_MANAGEMENT_ADDRESS_FIELDS = uniq(
  Object.values(NON_STANDARD_MANAGEMENT_ADDRESS_MAPPING)
    .filter(Boolean)
) as string[];

// Read-only compatibility. Huawei backends created before VSTOR-144517 kept the management
// address in metro_san_address, which is really an optional hypermetro peer address rather than
// a management address. Both Huawei drivers now write san_ip like every other driver, so these
// fields are only ever read as a fallback. They stay in NON_STANDARD_FIELD_NAMES so the edit
// dialog neither exposes them as custom driver params nor drops them from the payload.
export const LEGACY_MANAGEMENT_ADDRESS_MAPPING = {
  'Huawei iSCSI': 'metro_san_address',
  'Huawei iSCSI (legacy)': 'metro_san_address',
  'Huawei FC': 'metro_san_address',
  'Huawei FC (legacy)': 'metro_san_address'
};

export const LEGACY_MANAGEMENT_ADDRESS_FIELDS = uniq(
  Object.values(LEGACY_MANAGEMENT_ADDRESS_MAPPING)
    .filter(Boolean)
) as string[];

export const NON_STANDARD_STORAGE_PROTOCOL_FIELDS = uniq(
  Object.values(NON_STANDARD_DRIVER_PROTOCOL_FIELD_MAPPING)
    .filter(Boolean)
) as string[];

// TODO: add cinder.volume.drivers.hitachi.hbsd_iscsi.HBSDISCSIDriver when it is supported by OpenStack
export const DRIVER_SSL_PROPERTIES_MAPPING = {
  'cinder.volume.drivers.synology.synology_iscsi.SynoISCSIDriver': {
    synology_ssl_verify: true
  },
  'cinder.volume.drivers.zadara.zadara.ZadaraVPSAISCSIDriver': {
    zadara_vpsa_use_ssl: true,
    zadara_ssl_cert_verify: true
  }
};

export const NON_STANDARD_FIELD_NAMES = [
  ...NON_STANDARD_MANAGEMENT_ADDRESS_FIELDS,
  ...LEGACY_MANAGEMENT_ADDRESS_FIELDS,
  ...NON_STANDARD_STORAGE_PROTOCOL_FIELDS,
  ...Object.values(DRIVER_SSL_PROPERTIES_MAPPING).flatMap(props => Object.keys(props))
];
